// useLead — shared data hook for the Lead Detail page and the compact
// LeadDrawer quick-view. Both surfaces read the same tenant-scoped
// GET /api/leads/:id payload ({ lead, messages, calls }) so they never drift.
//
// Extracted from LeadDrawer so the new 3-column LeadDetail page and the drawer
// share one source of truth (and one refetch cadence).

import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';

/**
 * @param {string} leadId
 * @param {object} [opts]
 * @param {number} [opts.refetchInterval=5000] — poll cadence in ms (0 disables).
 * @returns {{ lead, messages, calls, emailMessages, isLoading, error, refetch, data }}
 */
export function useLead(leadId, { refetchInterval = 5000 } = {}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => apiGet(`/leads/${leadId}`),
    refetchInterval: refetchInterval || false,
    enabled: !!leadId,
  });

  const lead = data?.lead || null;
  const messages = data?.messages || [];
  const calls = data?.calls || [];
  const emailMessages = messages.filter((m) => m.channel === 'email');

  return { lead, messages, calls, emailMessages, isLoading, error, refetch, data };
}

export default useLead;
